import WhatshotIcon from "@mui/icons-material/Whatshot";
import NightlifeIcon from "@mui/icons-material/Nightlife";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import HighlightIcon from "@mui/icons-material/Highlight";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import AnimationIcon from "@mui/icons-material/Animation";
import AlignVerticalCenterIcon from "@mui/icons-material/AlignVerticalCenter";
import CoronavirusIcon from "@mui/icons-material/Coronavirus";
import MedicationIcon from "@mui/icons-material/Medication";
import Box from "@mui/joy/Box";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton from "@mui/joy/ListItemButton";
import ListItemContent from "@mui/joy/ListItemContent";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import Typography from "@mui/joy/Typography";
import { DisplayProofChipSet } from "../../../shared/components/ui/QuestionAnswering/DisplayAnswerFacts";
import { ProofChip } from "../../../shared/components/ui/QuestionAnswering/ProofChip";
import { Episode } from "../../../models/clinical_event";
import { PatientView } from "../../../models/view";
import { episodeLabel } from "../../../shared/utils/question_answering";
import { AnswerType } from "../../../models/questionnaire";

interface CancerPanelProps {
  readonly patientView: PatientView;
}

interface PanelItem {
  icon: JSX.Element;
  title: string;
  events: any[];
  answerType?: AnswerType;
  render?: (events: Episode[]) => JSX.Element;
}

const renderEventContent = (
  events: any[],
  render?: (events: Episode[]) => JSX.Element,
  answerType?: AnswerType
) => {
  if (events.length === 0) {
    return <small>-</small>;
  }

  if (render) {
    return render(events);
  }

  return <DisplayProofChipSet events={events} answerType={answerType!} />;
};

export default function CancerPanel({ patientView }: CancerPanelProps) {
  const panelItems: PanelItem[] = [
    {
      icon: <WhatshotIcon />,
      title: "Cancer type",
      events: patientView.cancer_facts,
      answerType: "concept",
    },
    {
      icon: <AllInclusiveIcon />,
      title: "Genetic variants",
      events: patientView.mutation_facts,
      answerType: "concept",
    },
    {
      icon: <HighlightIcon />,
      title: "Tumor markers",
      events: patientView.tumor_marker_facts,
      answerType: "concept-date-value",
    },
    {
      icon: <AnimationIcon />,
      title: "Metastasis",
      events: patientView.metastasis_facts,
      answerType: "concept",
    },
    {
      icon: <AlignVerticalCenterIcon />,
      title: "TNM",
      events: patientView.tnm_facts,
      answerType: "date-value",
    },
    {
      icon: <MedicationIcon />,
      title: "Episodes of treatment",
      events: patientView.episode_cancer_treatment,
      // Custom renderer for episodes
      render: (events: Episode[]) => (
        <fieldset
          aria-labelledby="proof-chip-set"
          style={{
            border: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
          }}
        >
          {events.map((episode: Episode, index: number) => (
            <ProofChip
              key={`episode-${index}-${episode.probability || "unknown"}`}
              name={episodeLabel(episode)}
              probability={episode.probability}
              eventSources={episode.source}
            />
          ))}
        </fieldset>
      ),
    },
    {
      icon: <ContentCutIcon />,
      title: "Surgery",
      events: patientView.surgery,
      answerType: "concept-date",
    },
    {
      icon: <SettingsInputAntennaIcon />,
      title: "Radiotherapy",
      events: patientView.radiotherapy,
      answerType: "concept-date",
    },
    {
      icon: <CoronavirusIcon />,
      title: "Comorbidities",
      events: patientView.comorbidities_facts,
      answerType: "concept",
    },
    {
      icon: <NightlifeIcon />,
      title: "Life habits",
      events: patientView.life_habits,
      answerType: "concept",
    },
  ];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography level="title-lg">Cancer panel</Typography>
        <Box>
          <List>
            {panelItems.map(({ icon, title, events, answerType, render }, index) => (
              <ListItem key={`${title}-${events.length}`}>
                <ListItemButton variant="plain">
                  <ListItemDecorator>{icon}</ListItemDecorator>
                  <ListItemContent>
                    <Typography level="title-sm">{title}</Typography>
                    <Typography level="body-sm">
                      {renderEventContent(events, render, answerType)}
                    </Typography>
                  </ListItemContent>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </CardContent>
    </Card>
  );
}
